import React, { useMemo, useState } from "react";
import { Reveal } from "@/components/site/primitives";
import { SEASONALITY, MONTHS } from "@/content/whereToBuy";
import { cn } from "@/lib/utils";

/**
 * SeasonalityChart — month-by-month crop availability timeline.
 * Only months with at least one crop in season are shown, so the chart stays condensed.
 * @param rows  [{ crop, months: [1-12] }]
 */
export const SeasonalityChart = ({
  id = "seasonality",
  rows = SEASONALITY,
  eyebrow = "What's fresh",
  title = "In season, month by month",
  intro = "Pick a month to see which crops are at their peak.",
  note,
  className = "bg-[var(--wf-cream)]",
  testidPrefix = "wtb",
}) => {
  // Months (1-12) that have at least one crop in season, in calendar order.
  const activeMonths = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => row.months.forEach((m) => set.add(m)));
    const list = [...set].sort((a, b) => a - b);
    return list.length > 0 ? list : MONTHS.map((_, i) => i + 1);
  }, [rows]);

  const currentMonth = new Date().getMonth() + 1;
  const [selected, setSelected] = useState(activeMonths.includes(currentMonth) ? currentMonth : activeMonths[0]);
  const inSeasonNow = rows.filter((row) => row.months.includes(selected));
  const colCount = activeMonths.length;
  const gridTemplate = { gridTemplateColumns: `minmax(150px, 180px) repeat(${colCount}, minmax(0, 1fr))` };

  return (
    <section id={id} aria-labelledby={`${id}-heading`} className={cn("wf-section scroll-mt-20", className)}>
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">{eyebrow}</span>
          <h2 id={`${id}-heading`} className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">{title}</h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">{intro}</p>
        </Reveal>

        {/* Month selector (only months with crops in season) */}
        <div className="mt-8 flex flex-wrap justify-center gap-2" role="group" aria-label="Select a month">
          {activeMonths.map((month) => {
            const m = MONTHS[month - 1];
            const active = month === selected;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setSelected(month)}
                aria-pressed={active}
                data-testid={`${testidPrefix}-month-${m.toLowerCase()}`}
                className={cn(
                  "min-w-[3.25rem] rounded-full px-3 py-2 text-xs font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                  active
                    ? "bg-[var(--wf-maroon)] text-[var(--wf-paper)]"
                    : "border border-[var(--wf-border)] bg-[var(--wf-paper)] text-[var(--wf-soil)] hover:border-[var(--wf-maroon)]"
                )}
              >
                {m}
              </button>
            );
          })}
        </div>

        {/* Summary for selected month */}
        <Reveal className="mx-auto mt-8 max-w-3xl rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-6 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wf-green-text)]">In season in {MONTHS[selected - 1]}</p>
          {inSeasonNow.length > 0 ? (
            <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid={`${testidPrefix}-in-season-list`}>
              {inSeasonNow.map((row) => (
                <span key={row.crop} className="rounded-full bg-[var(--wf-wheat)] px-3 py-1.5 text-sm font-medium text-[var(--wf-maroon)]">{row.crop}</span>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground" data-testid={`${testidPrefix}-in-season-list`}>Fields are resting this month — check back in the growing season.</p>
          )}
        </Reveal>

        {/* Full chart */}
        <div className="mt-10 overflow-x-auto">
          <div className="min-w-[560px]" role="table" aria-label="Crop availability by month" data-testid={`${testidPrefix}-seasonality-chart`}>
            {/* Header row */}
            <div className="grid items-center gap-1.5" style={gridTemplate} role="row">
              <span className="pr-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground" role="columnheader">Crop</span>
              {activeMonths.map((month) => {
                const m = MONTHS[month - 1];
                return (
                  <button
                    key={m}
                    type="button"
                    role="columnheader"
                    onClick={() => setSelected(month)}
                    aria-pressed={month === selected}
                    data-testid={`${testidPrefix}-chart-month-${m.toLowerCase()}`}
                    className={cn(
                      "rounded-sm py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                      month === selected ? "bg-[var(--wf-maroon)] text-[var(--wf-paper)]" : "text-muted-foreground hover:text-[var(--wf-maroon)]"
                    )}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            {/* Crop rows */}
            <div className="mt-2 space-y-1.5">
              {rows.map((row) => (
                <div key={row.crop} className="grid items-center gap-1.5" style={gridTemplate} role="row">
                  <span className="truncate pr-3 text-sm font-medium text-[var(--wf-ink)]" role="rowheader">{row.crop}</span>
                  {activeMonths.map((month) => {
                    const m = MONTHS[month - 1];
                    const on = row.months.includes(month);
                    const isSel = month === selected;
                    return (
                      <div
                        key={m}
                        role="cell"
                        title={`${row.crop} · ${m}${on ? " · in season" : ""}`}
                        className={cn(
                          "h-6 rounded-sm transition-colors",
                          on ? "bg-[var(--wf-farm-green)]" : "bg-[var(--wf-border)]/50",
                          isSel && "ring-2 ring-[var(--wf-maroon)] ring-offset-1 ring-offset-[var(--wf-cream)]",
                          on && isSel && "bg-[var(--wf-maroon)]"
                        )}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            {/* Legend */}
            <div className="mt-5 flex items-center gap-5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-2"><span className="h-3.5 w-5 rounded-sm bg-[var(--wf-farm-green)]" aria-hidden="true" /> In season</span>
              <span className="inline-flex items-center gap-2"><span className="h-3.5 w-5 rounded-sm bg-[var(--wf-border)]/50" aria-hidden="true" /> Out of season</span>
            </div>
            {note && <p className="mt-4 text-xs leading-5 text-muted-foreground" data-testid={`${testidPrefix}-seasonality-note`}>{note}</p>}
          </div>
        </div>
      </div>
    </section>
  );
};
